import { WifiOff } from 'lucide-react';
import { useConnectionState } from '@/lib/auth';
import { CONNECTION_COPY } from '@/lib/sessionGate';

/**
 * "The server is not answering."
 *
 * Scope is deliberately one thing. Ordinary 4xx errors are reported by the
 * screen that caused them, and an expired session is handled by an in-place
 * password dialog — neither belongs in a strip at the top of the window.
 *
 * What it must never do is navigate, and what it must never say is that the
 * work will be saved later. LeadForge has no mutation queue: a form that
 * failed to submit has not been recorded anywhere, and the only thing keeping
 * it alive is the browser tab staying open. The copy says exactly that. See
 * CONNECTION_COPY in lib/sessionGate.ts, where a test guards the wording.
 *
 * It clears itself: the next request that gets any HTTP response at all — even
 * a 500 — proves the connection is back.
 */
export function ConnectionBanner(): JSX.Element | null {
  const state = useConnectionState();
  if (state !== 'offline') return null;

  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-2xs text-amber-200"
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1">{CONNECTION_COPY.offline}</span>
    </div>
  );
}
